import React from 'react'
import { resourcesInverted, translateResourceColorRaw } from './translateResource'

export interface ParsedMessagePart {
  text: string
  type: 'text' | 'board' | 'currency' | 'resource' | 'other'
  data?: any
}

export interface MessageParserConfig {
  board?: Record<string, any>
  currencyPattern?: RegExp
  focusPlanet?: (planet: string) => void
  customParsers?: Array<{
    name: string
    pattern: RegExp | ((word: string) => boolean)
    render: (text: string, data?: any) => React.ReactNode
  }>
}

/**
 * Parses a message string and identifies different types of content
 * that should be styled or handled differently
 */
export function parseMessage(message: string, config: MessageParserConfig = {}): ParsedMessagePart[] {
  const { board, currencyPattern = /^\$[\d,]+(\.\d{2})?$/, customParsers = [] } = config
  
  // Split on whitespace but preserve the whitespace
  const parts = message.split(/(\s+)/)
  const result: ParsedMessagePart[] = []
  
  for (const part of parts) {
    // Skip empty parts
    if (!part) continue
    
    // If it's whitespace, keep it as text
    if (/^\s+$/.test(part)) {
      result.push({ text: part, type: 'text' })
      continue
    }
    
    // Clean the word for lookup (remove punctuation from end)
    const cleanWord = part.replace(/[.,!?;:]+$/, '')
    let matched = false
    
    // Check board first - simple direct lookup
    if (board && board[cleanWord]) {
      result.push({
        text: part,
        type: 'board',
        data: board[cleanWord]
      })
      matched = true
    }
    // Check if it's a resource (both full names and abbreviations)
    else if (resourcesInverted[cleanWord]) {
      const resourceKey = resourcesInverted[cleanWord] || cleanWord
      result.push({
        text: part,
        type: 'resource',
        data: { resourceKey, fullName: cleanWord }
      })
      matched = true
    }
    // Check currency pattern
    else if (currencyPattern.test(cleanWord)) {
      result.push({
        text: part,
        type: 'currency'
      })
      matched = true
    }
    // Check custom parsers
    else {
      for (const parser of customParsers) {
        const isMatch = typeof parser.pattern === 'function' 
          ? parser.pattern(cleanWord)
          : parser.pattern.test(cleanWord)
        
        if (isMatch) {
          result.push({ 
            text: part, 
            type: 'other', 
            data: { parserName: parser.name }
          })
          matched = true
          break
        }
      }
    }
    
    // If no match, it's regular text
    if (!matched) {
      result.push({ text: part, type: 'text' })
    }
  }
  
  return result
}

/**
 * Renders parsed message parts as React elements
 */
export function renderParsedMessage(
  parts: ParsedMessagePart[], 
  config: MessageParserConfig = {}
): React.ReactNode {
  const { customParsers = [] } = config
  
  return parts.map((part, index) => {
    switch (part.type) {
      case 'board':
        return (
          <span 
            key={index}
            style={{ 
              color: 'var(--prometheus-primary)', 
              fontWeight: 'bold',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
            onClick={() => config?.focusPlanet?.(part.text)}
            title={`Board location: ${part.text.replace(/[.,!?;:]+$/, '')}`}
          >
            {part.text.split(':')[1].split('|')[0]}
          </span>
        )
      
      case 'resource':
        const resourceColor = translateResourceColorRaw(part.data?.resourceKey)
        return (
          <span
            key={index}
            style={{
              color: resourceColor || '#666',
              fontWeight: 'bold'
            }}
            title={`Resource: ${part.data?.fullName || part.text.replace(/[.,!?;:]+$/, '')}`}
          >
            {part.text}
          </span>
        )
      
      case 'currency':
        return (
          <span 
            key={index}
            style={{ 
              color: '#FF9800', 
              fontWeight: 'bold'
            }}
          >
            {part.text}
          </span>
        )
      
      case 'other':
        // Find the custom parser and use its render function
        const parser = customParsers.find(p => p.name === part.data?.parserName)
        if (parser && parser.render) {
          return <React.Fragment key={index}>{parser.render(part.text, part.data)}</React.Fragment>
        }
        return <span key={index}>{part.text}</span>
      
      case 'text':
      default:
        return <React.Fragment key={index}>{part.text}</React.Fragment>
    }
  })
}

/**
 * Convenience function that parses and renders a message in one call
 */
export function parseAndRenderMessage(
  message: string, 
  config: MessageParserConfig = {}
): React.ReactNode {
  const parts = parseMessage(message, config)
  return renderParsedMessage(parts, config)
}
